import { useEffect, useState } from 'react'
import { Plus, Pencil, Download, HardDrive, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react'
import {
  getCompanySettings, updateCompanySettings,
  getTeamMembers, addTeamMember, updateTeamMember,
  getExpenseCategories, addExpenseCategory, updateExpenseCategory,
  getFullBackup, getDriveStatus, driveConnectUrl,
} from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const TABS = ['Company', 'Team', 'Categories', 'Drive', 'Backup']
// Tabs only ADMINs may see
const ADMIN_ONLY_TABS = ['Drive', 'Backup']

export default function Settings() {
  const [tab, setTab] = useState('Company')
  const { user } = useAuth()

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
      <div className="flex gap-1 border-b">
        {TABS.filter(t => !ADMIN_ONLY_TABS.includes(t) || user?.role === 'ADMIN').map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === t ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Company' && <CompanySettings />}
      {tab === 'Team' && <TeamSettings />}
      {tab === 'Categories' && <CategorySettings />}
      {tab === 'Drive' && <DriveSettings />}
      {tab === 'Backup' && <BackupSettings />}
    </div>
  )
}

function CompanySettings() {
  const [form, setForm] = useState({ brandName: '', name: '', gstin: '', pan: '', cin: '', address: '', city: '', state: '', pincode: '', bankName: '', accountName: '', accountNumber: '', ifscCode: '', bankBranch: '', swiftCode: '', micrCode: '', email: '', phone: '', website: '' })
  const [saving, setSaving] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    getCompanySettings().then((res) => { if (res.data) setForm((f) => ({ ...f, ...res.data })); setLoaded(true) }).catch(() => setLoaded(true))
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try { await updateCompanySettings(form) } catch { alert('Failed to save') }
    finally { setSaving(false) }
  }

  if (!loaded) return <div className="animate-pulse h-40 bg-gray-100 rounded" />

  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value })

  return (
    <form onSubmit={handleSubmit} className="card space-y-4 max-w-3xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="label">Brand Name</label><input className="input" value={form.brandName} onChange={set('brandName')} /></div>
        <div><label className="label">Legal Name</label><input className="input" value={form.name} onChange={set('name')} /></div>
        <div><label className="label">GSTIN</label><input className="input font-mono" value={form.gstin} onChange={set('gstin')} /></div>
        <div><label className="label">PAN</label><input className="input font-mono" value={form.pan} onChange={set('pan')} /></div>
        <div><label className="label">CIN</label><input className="input font-mono" value={form.cin} onChange={set('cin')} /></div>
        <div><label className="label">Email</label><input className="input" value={form.email} onChange={set('email')} /></div>
        <div><label className="label">Phone</label><input className="input" value={form.phone} onChange={set('phone')} /></div>
        <div><label className="label">Website</label><input className="input" value={form.website} onChange={set('website')} /></div>
      </div>
      <div><label className="label">Address</label><textarea className="input" rows={2} value={form.address} onChange={set('address')} /></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div><label className="label">City</label><input className="input" value={form.city} onChange={set('city')} /></div>
        <div><label className="label">State</label><input className="input" value={form.state} onChange={set('state')} /></div>
        <div><label className="label">Pincode</label><input className="input" value={form.pincode} onChange={set('pincode')} /></div>
      </div>
      <h3 className="text-sm font-semibold text-gray-700 pt-2">Bank Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="label">Bank Name</label><input className="input" value={form.bankName} onChange={set('bankName')} /></div>
        <div><label className="label">Account Name</label><input className="input" value={form.accountName} onChange={set('accountName')} /></div>
        <div><label className="label">Account No.</label><input className="input font-mono" value={form.accountNumber} onChange={set('accountNumber')} /></div>
        <div><label className="label">IFSC</label><input className="input font-mono" value={form.ifscCode} onChange={set('ifscCode')} /></div>
        <div><label className="label">Branch</label><input className="input" value={form.bankBranch} onChange={set('bankBranch')} /></div>
        <div><label className="label">SWIFT Code</label><input className="input font-mono" value={form.swiftCode} onChange={set('swiftCode')} /></div>
        <div><label className="label">MICR Code</label><input className="input font-mono" value={form.micrCode} onChange={set('micrCode')} /></div>
      </div>
      <div className="flex justify-end pt-2">
        <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving...' : 'Save'}</button>
      </div>
    </form>
  )
}

function TeamSettings() {
  const [members, setMembers] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'STAFF' })
  const [editId, setEditId] = useState(null)

  const load = () => getTeamMembers().then((res) => setMembers(res.data)).catch(() => {})
  useEffect(() => { load() }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editId) await updateTeamMember(editId, form)
      else await addTeamMember(form)
      setShowForm(false)
      setEditId(null)
      setForm({ name: '', email: '', phone: '', role: 'STAFF' })
      load()
    } catch (err) { alert(err.response?.data?.error || 'Failed') }
  }

  const startEdit = (m) => {
    setEditId(m.id)
    setForm({ name: m.name, email: m.email, phone: m.phone || '', role: m.role })
    setShowForm(true)
  }

  const toggleActive = async (m) => {
    if (!confirm(`${m.isActive ? 'Deactivate' : 'Reactivate'} ${m.name}? They ${m.isActive ? 'will no longer be able to' : 'will be able to'} log in.`)) return
    try {
      await updateTeamMember(m.id, { isActive: !m.isActive })
      load()
    } catch { alert('Failed') }
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Team members can log in to submit expenses and create invoices.</p>
        <button onClick={() => { setEditId(null); setForm({ name: '', email: '', phone: '', role: 'STAFF' }); setShowForm(true) }} className="btn-primary flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> Add Member
        </button>
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="card space-y-4">
          <h3 className="text-sm font-semibold text-gray-700">{editId ? 'Edit Team Member' : 'Add Team Member'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="label">Name *</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div><label className="label">Email * <span className="text-gray-400 font-normal">(must match Google login)</span></label><input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
            <div><label className="label">Phone</label><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91..." /></div>
            <div>
              <label className="label">Role</label>
              <select className="select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="ADMIN">Admin — Full access</option>
                <option value="STAFF">Staff — Expenses + own invoices</option>
                <option value="INVOICE_CREATOR">Invoice Creator — Create invoices only, no financials</option>
                <option value="VIEWER">Viewer — Read-only</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary">{editId ? 'Update' : 'Add Member'}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditId(null) }} className="btn-secondary">Cancel</button>
          </div>
        </form>
      )}
      <div className="card overflow-hidden !p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 bg-gray-50/80 border-b">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className={`border-b last:border-0 ${!m.isActive ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 font-medium">{m.name}</td>
                <td className="px-4 py-3 text-gray-500">{m.email}</td>
                <td className="px-4 py-3 text-gray-500">{m.phone || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                    m.role === 'ADMIN' ? 'bg-brand-50 text-brand-700' :
                    m.role === 'STAFF' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
                  }`}>{m.role}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs ${m.isActive ? 'text-emerald-600' : 'text-gray-400'}`}>
                    {m.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => startEdit(m)} className="text-gray-400 hover:text-gray-600" title="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => toggleActive(m)} className={`text-xs font-medium ${m.isActive ? 'text-red-500 hover:text-red-700' : 'text-emerald-500 hover:text-emerald-700'}`}>
                      {m.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DriveSettings() {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = () => {
    setLoading(true)
    getDriveStatus()
      .then((res) => setStatus(res.data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  // Surface the ?drive=... result after returning from the OAuth callback
  const params = new URLSearchParams(window.location.search)
  const driveResult = params.get('drive')

  const resultBanner = {
    connected:        { type: 'success', msg: 'Google Drive connected successfully.' },
    denied:           { type: 'error',   msg: 'You cancelled the Google sign-in.' },
    wrong_account:    { type: 'error',   msg: 'Wrong account. You must sign in as leeky@osteo3d.com — the owner of the Books Drive folder.' },
    no_refresh_token: { type: 'error',   msg: 'Google did not return a refresh token. Try again and click "Allow".' },
    invalid_state:    { type: 'error',   msg: 'The sign-in session expired. Please try again.' },
    error:            { type: 'error',   msg: 'Something went wrong connecting Drive. Please try again.' },
  }[driveResult]

  const connect = () => { window.location.href = driveConnectUrl() }

  const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'

  return (
    <div className="space-y-6 max-w-2xl">
      {resultBanner && (
        <div className={`rounded-lg px-4 py-3 text-sm ${resultBanner.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {resultBanner.msg}
        </div>
      )}

      <div className="card space-y-4">
        <div className="flex items-start gap-3">
          <HardDrive className="h-5 w-5 text-gray-400 mt-0.5" />
          <div>
            <h2 className="text-base font-semibold text-gray-900">Google Drive</h2>
            <p className="text-sm text-gray-500 mt-1">
              Approved invoice, estimate and delivery-challan PDFs are uploaded to the
              <strong> Osteo3d Books</strong> folder in Google Drive for your CA. This must be
              connected as <strong>leeky@osteo3d.com</strong> (the folder owner).
            </p>
          </div>
        </div>

        {loading ? (
          <div className="animate-pulse h-16 bg-gray-100 rounded" />
        ) : (
          <div className="rounded-lg border border-gray-200 px-4 py-3 space-y-1">
            {status?.connected ? (
              <>
                <div className="flex items-center gap-2 text-sm font-medium">
                  {status.healthy ? (
                    <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> <span className="text-emerald-700">Connected</span></>
                  ) : (
                    <><AlertTriangle className="h-4 w-4 text-amber-600" /> <span className="text-amber-700">Connected as the wrong account</span></>
                  )}
                </div>
                <p className="text-sm text-gray-500">Account: <span className="font-mono">{status.email || 'unknown'}</span></p>
                <p className="text-sm text-gray-500">Last updated: {fmt(status.updatedAt)}</p>
                {!status.healthy && (
                  <p className="text-sm text-amber-700">
                    Expected <span className="font-mono">{status.expectedIdentity}</span>. Reconnect as that account.
                  </p>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm font-medium text-red-700">
                <AlertTriangle className="h-4 w-4" /> Not connected — uploads are failing
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button onClick={connect} className="btn-primary flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            {status?.connected ? 'Reconnect Google Drive' : 'Connect Google Drive'}
          </button>
          <button onClick={load} className="btn-secondary">Refresh status</button>
        </div>
        <p className="text-xs text-gray-400">
          You'll be sent to Google to sign in as leeky@osteo3d.com and grant Drive access, then returned here.
        </p>
      </div>
    </div>
  )
}

function BackupSettings() {
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState(null)

  const downloadFullBackup = async () => {
    setLoading(true)
    setStatus(null)
    try {
      const res = await getFullBackup()
      const { files, generatedAt } = res.data
      const ts = generatedAt.slice(0, 10)

      // Trigger one CSV download per file
      for (const [, file] of Object.entries(files)) {
        const blob = new Blob([file.content], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = file.filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        // Brief gap so browser doesn't block multiple downloads
        await new Promise(r => setTimeout(r, 120))
      }

      const fileCount = Object.keys(files).length
      setStatus({ type: 'success', msg: `${fileCount} CSV files downloaded (${ts})` })
    } catch (err) {
      setStatus({ type: 'error', msg: err.response?.data?.error || 'Export failed' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="card space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Full Data Export</h2>
          <p className="text-sm text-gray-500 mt-1">
            Downloads all your data as CSV files — invoices, estimates, delivery challans, credit notes,
            payments, customers, and expenses. Use this to migrate to another tool or keep an offline copy.
          </p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          Your browser will download <strong>11 CSV files</strong>. Allow multiple downloads if prompted.
        </div>
        <div className="flex items-center gap-4">
          <button onClick={downloadFullBackup} disabled={loading}
            className="btn-primary flex items-center gap-2">
            <Download className="h-4 w-4" />
            {loading ? 'Preparing export…' : 'Download Full Backup'}
          </button>
          {status && (
            <p className={`text-sm ${status.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
              {status.msg}
            </p>
          )}
        </div>
      </div>

      <div className="card space-y-3">
        <h2 className="text-base font-semibold text-gray-900">Database Backup (DB Admin)</h2>
        <p className="text-sm text-gray-500">
          For a full PostgreSQL dump (including all history and IDs), run this from the repo root:
        </p>
        <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg px-4 py-3 overflow-x-auto">
          ./backend-books/scripts/backup.sh
        </pre>
        <p className="text-sm text-gray-500">
          Saves a <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">.dump</code> file to{' '}
          <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">~/Documents/Backups/osteo3d-books/</code>.
          Runs automatically every Sunday at 8 AM via macOS launchd.
        </p>
      </div>
    </div>
  )
}

function CategorySettings() {
  const [categories, setCategories] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ name: '', tallyLedgerName: '', tallyGroup: 'Indirect Expenses' })
  const [editId, setEditId] = useState(null)

  const load = () => getExpenseCategories().then((res) => setCategories(res.data)).catch(() => {})
  useEffect(() => { load() }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editId) await updateExpenseCategory(editId, form)
      else await addExpenseCategory(form)
      setShowForm(false)
      setEditId(null)
      setForm({ name: '', tallyLedgerName: '', tallyGroup: 'Indirect Expenses' })
      load()
    } catch (err) { alert(err.response?.data?.error || 'Failed') }
  }

  const startEdit = (c) => { setEditId(c.id); setForm({ name: c.name, tallyLedgerName: c.tallyLedgerName || '', tallyGroup: c.tallyGroup || 'Indirect Expenses' }); setShowForm(true) }

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex justify-end">
        <button onClick={() => { setEditId(null); setForm({ name: '', tallyLedgerName: '', tallyGroup: 'Indirect Expenses' }); setShowForm(true) }} className="btn-primary flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> Add Category
        </button>
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="card space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><label className="label">Name</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div><label className="label">Tally Ledger Name</label><input className="input" value={form.tallyLedgerName} onChange={(e) => setForm({ ...form, tallyLedgerName: e.target.value })} required /></div>
            {/* The API has always required this; the form never sent it, so every
                "Add Category" failed with a 400. It also decides how the CA books
                the spend, which is not a field to leave to a silent default. */}
            <div>
              <label className="label">Tally Group</label>
              <select className="select" value={form.tallyGroup}
                onChange={(e) => setForm({ ...form, tallyGroup: e.target.value })} required>
                <option value="Indirect Expenses">Indirect Expenses — overheads</option>
                <option value="Direct Expenses">Direct Expenses — cost of delivering a case</option>
                <option value="Fixed Assets">Fixed Assets — capitalised and depreciated</option>
                <option value="Duties &amp; Taxes">Duties &amp; Taxes — settles a liability</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2"><button type="submit" className="btn-primary">{editId ? 'Update' : 'Add'}</button><button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button></div>
        </form>
      )}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-500 bg-gray-50 border-b"><th className="px-4 py-3 font-medium">Category</th><th className="px-4 py-3 font-medium">Tally Ledger</th><th className="px-4 py-3 font-medium">Tally Group</th><th className="px-4 py-3" /></tr></thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.id} className="border-b last:border-0"><td className="px-4 py-3">{c.name}</td><td className="px-4 py-3 text-gray-500">{c.tallyLedgerName || '—'}</td><td className="px-4 py-3 text-gray-500">{c.tallyGroup || '—'}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => startEdit(c)} className="text-gray-400 hover:text-gray-600"><Pencil className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
